import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const focusable =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex="0"]';
/** Applies the same keyboard contract to the existing modal surfaces. */
export function PageExperience() {
  const location = useLocation();
  useEffect(() => {
    let focused = false;
    const update = () => {
      const heading = document.querySelector<HTMLElement>("h1");
      if (!heading) return;
      document.title = `${heading.textContent?.trim() || "4 Apex V2"} | 4 Apex V2`;
      if (!focused) {
        heading.tabIndex = -1;
        heading.focus({ preventScroll: true });
        focused = true;
      }
    };
    const observer = new MutationObserver(update);
    observer.observe(document.body, { childList: true, subtree: true });
    update();
    return () => observer.disconnect();
  }, [location.pathname, location.search]);
  useEffect(() => {
    let dialog: HTMLElement | null = null,
      previous: HTMLElement | null = null;
    const detect = () => {
      const next =
        [
          ...document.querySelectorAll<HTMLElement>(
            '[role="dialog"][aria-modal="true"]',
          ),
        ].at(-1) ?? null;
      if (next === dialog) return;
      if (next) {
        previous = document.activeElement as HTMLElement;
        dialog = next;
        const first = dialog.querySelector<HTMLElement>(focusable);
        (first ?? dialog).focus();
      } else {
        dialog = null;
        if (previous?.isConnected) previous.focus();
        previous = null;
      }
    };
    const keys = (event: KeyboardEvent) => {
      if (!dialog) return;
      const items = [...dialog.querySelectorAll<HTMLElement>(focusable)].filter(
        (e) => e.getClientRects().length > 0,
      );
      if (event.key === "Escape") {
        const close = items.find(
          (e) =>
            /^Fechar/.test(e.getAttribute("aria-label") ?? "") ||
            e.textContent?.trim() === "Cancelar",
        );
        if (close) {
          event.preventDefault();
          close.click();
        }
      }
      if (event.key === "Tab" && items.length) {
        const first = items[0],
          last = items[items.length - 1];
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            !dialog.contains(document.activeElement))
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            !dialog.contains(document.activeElement))
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    const focus = (event: FocusEvent) => {
      if (dialog && !dialog.contains(event.target as Node))
        dialog.querySelector<HTMLElement>(focusable)?.focus();
    };
    const observer = new MutationObserver(detect);
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener("keydown", keys);
    document.addEventListener("focusin", focus);
    detect();
    return () => {
      observer.disconnect();
      document.removeEventListener("keydown", keys);
      document.removeEventListener("focusin", focus);
    };
  }, []);
  return null;
}
